import PropTypes from 'prop-types';
import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useSwipeable } from 'react-swipeable';

import { SQUARE } from '@utils/image';
import { isBrowser } from '@utils/environmentCheck';

import useIsomorphicLayoutEffect from '@common/hooks/useIsomorphicLayoutEffect';
import { Chevron } from '@common/components/Icons';
import LazyImg from '@common/components/LazyImg';
import { useCarouselContext } from '@common/contexts/carouselContext';

import {
  ModalStyled,
  SwipeHandlerStyled,
  ZoomModalBody,
  ZoomModalNavButton,
  ZoomModalPill,
} from './ZoomImageModal.styled';

const MODAL_TIMEOUT = 500;
const MAX_IMAGE_SIZE = 3000;

const ZoomImageModal = ({
  imageAltText,
  images,
  initialZoomModalIndex,
  isModalOpen,
  setIsModalOpen,
}) => {
  const zoomTransformWrapperRef = useRef(null);
  const [zoomModalIndex, setZoomModalIndex] = useState(
    initialZoomModalIndex || 0,
  );
  const [isGrabbing, setIsGrabbing] = useState(false);
  const carouselContext = useCarouselContext();
  const viewportWidth = isBrowser ? document.documentElement.clientWidth : 600;
  const viewportHeight = isBrowser
    ? document.documentElement.clientHeight
    : 600;
  const finalImageWidth =
    viewportWidth > MAX_IMAGE_SIZE ? MAX_IMAGE_SIZE : viewportWidth;
  const finalImageHeight =
    viewportHeight > MAX_IMAGE_SIZE ? MAX_IMAGE_SIZE : viewportHeight;
  const zoomedOutSize = Math.min(finalImageWidth, finalImageHeight);
  const maxScale = MAX_IMAGE_SIZE / zoomedOutSize;

  useIsomorphicLayoutEffect(() => {
    if (initialZoomModalIndex !== undefined) {
      setZoomModalIndex(initialZoomModalIndex);
    }
  }, [initialZoomModalIndex]);

  const previousImage = useCallback(() => {
    const nextIndex =
      zoomModalIndex === 0 ? images.length - 1 : zoomModalIndex - 1;

    setZoomModalIndex(nextIndex);

    if (carouselContext.carousel) {
      carouselContext.carousel.slideTo(nextIndex);
    }
  }, [carouselContext.carousel, images.length, zoomModalIndex]);
  const nextImage = useCallback(() => {
    const nextIndex =
      zoomModalIndex === images.length - 1 ? 0 : zoomModalIndex + 1;

    setZoomModalIndex(nextIndex);

    if (carouselContext.carousel) {
      carouselContext.carousel.slideTo(nextIndex);
    }
  }, [images.length, setZoomModalIndex, zoomModalIndex]);

  const isZoomedOut = zoomTransformWrapperRef?.current?.state?.scale === 1;
  const handleKeyPress = useCallback(
    ({ keyCode }) => {
      if (keyCode === 39) {
        nextImage();
      } else if (keyCode === 37) {
        previousImage();
      } else if (keyCode === 27) {
        setIsModalOpen(false);
      }
    },
    [nextImage, previousImage, setIsModalOpen],
  );

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isZoomedOut) return;
      nextImage();
    },
    onSwipedRight: () => {
      if (!isZoomedOut) return;
      previousImage();
    },
    trackTouch: true,
  });

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <ModalStyled
      appear
      closeHasShadow
      exit
      enter
      isOpen={isModalOpen}
      mountOnEnter
      onClose={() => setIsModalOpen(false)}
      isSmallCloseButton
      timeout={MODAL_TIMEOUT}
      unmountOnExit
      isWhiteCloseButton
    >
      <ZoomModalBody isGrabbing={isGrabbing}>
        <TransformWrapper
          centerOnInit
          initialScale={1}
          minScale={1}
          maxScale={maxScale}
          panning={{
            velocityDisabled: false,
            disabled: !!isZoomedOut,
          }}
          onZoomStart={() => setIsGrabbing(true)}
          onPanningStart={() => setIsGrabbing(true)}
          onZoomStop={() => setIsGrabbing(false)}
          onPanningStop={() => setIsGrabbing(false)}
          ref={zoomTransformWrapperRef}
        >
          <TransformComponent wrapperClass="zoom-wrapper">
            {images[zoomModalIndex] && (
              <SwipeHandlerStyled {...swipeHandlers}>
                <LazyImg
                  breakpoints={[3000]}
                  aspectRatios={SQUARE}
                  src={images[zoomModalIndex]}
                  alt={imageAltText}
                  disableAbsolute
                  shouldLazyLoad
                  {...swipeHandlers}
                />
              </SwipeHandlerStyled>
            )}
          </TransformComponent>
        </TransformWrapper>
        {images.length > 1 && (
          <>
            <ZoomModalPill>{`${zoomModalIndex + 1}/${images.length}`}</ZoomModalPill>
            <ZoomModalNavButton
              direction="left"
              isWhite
              onClick={previousImage}
            >
              <Chevron />
            </ZoomModalNavButton>
            <ZoomModalNavButton
              direction="right"
              isFlipped
              isWhite
              onClick={nextImage}
            >
              <Chevron />
            </ZoomModalNavButton>
          </>
        )}
      </ZoomModalBody>
    </ModalStyled>
  );
};

ZoomImageModal.propTypes = {
  imageAltText: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialZoomModalIndex: PropTypes.number,
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
};

ZoomImageModal.defaultProps = {
  initialZoomModalIndex: 0,
};

export default ZoomImageModal;
